import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

export enum RegionGroup {
  APAC = 'apac',
  GCC = 'gcc',
  LATAM = 'latam',
  MENA = 'mena',
  NORTH_AMERICA = 'north-america',
  SUB_SAHARAN_AFRICA = 'sub-saharan-africa',
}

const GCC_COUNTRIES = new Set([
  'AE', // United Arab Emirates
  'BH', // Bahrain
  'KW', // Kuwait
  'OM', // Oman
  'QA', // Qatar
  'SA', // Saudi Arabia
]);

const MENA_COUNTRIES = new Set([
  'AE', // United Arab Emirates
  'BH', // Bahrain
  'DZ', // Algeria
  'EG', // Egypt
  'IL', // Israel
  'IQ', // Iraq
  'IR', // Iran (if you include it in MENA)
  'JO', // Jordan
  'KW', // Kuwait
  'LB', // Lebanon
  'LY', // Libya
  'MA', // Morocco
  'MR', // Mauritania (often counted in MENA)
  'OM', // Oman
  'PS', // Palestine
  'QA', // Qatar
  'SA', // Saudi Arabia
  'SD', // Sudan
  'SY', // Syria
  'TN', // Tunisia
  'YE', // Yemen
]);

const LATAM_COUNTRIES = new Set([
  'AG',
  'AR', // Argentina
  'BB',
  'BO', // Bolivia
  'BR', // Brazil
  'BS',
  'CL', // Chile
  'CO', // Colombia
  'CR', // Costa Rica
  'CU', // Cuba
  'DM',
  'DO', // Dominican Republic
  'EC', // Ecuador
  'GD',
  'GT', // Guatemala
  'HN', // Honduras
  'HT',
  'JM',
  'LC',
  'MX', // Mexico
  'NI', // Nicaragua 'PA', // Panama 'PE', // Peru 'PR', // Puerto Rico 'PY', // Paraguay 'SV', // El Salvador 'TT', 'UY', // Uruguay 'VC', 'VE', // Venezuela
]);

const APAC_COUNTRIES = new Set([
  'AF', // Oceania
  'AU',
  'BD', // Southeast Asia
  'BN',
  'BT', // East Asia
  'CN',
  'FJ',
  'FM',
  'HK',
  'ID',
  'IN',
  'JP',
  'KH',
  'KI',
  'KP',
  'KR',
  'LA',
  'LK',
  'MH',
  'MM',
  'MN',
  'MO',
  'MV',
  'MY',
  'NP',
  'NR',
  'NZ',
  'PG',
  'PH',
  'PK',
  'PW',
  'SB',
  'SG',
  'TH',
  'TL',
  'TO',
  'TW',
  'VN',
  'VU',
  'WS',
]);

const NORTH_AMERICA_COUNTRIES = new Set([
  'CA', // Canada
  'US', // United States
]);

const SUB_SAHARAN_AFRICA_COUNTRIES = new Set([
  // Central Africa
  'AO',
  'BF', // East Africa
  'BI', // West Africa
  'BJ', // Southern Africa
  'BW',
  'CD',
  'CF',
  'CG',
  'CI',
  'CM',
  'CV',
  'DJ',
  'ER',
  'ET',
  'GA',
  'GH',
  'GM',
  'GN',
  'GQ',
  'GW',
  'KE',
  'LR',
  'LS',
  'MG',
  'ML',
  'MR',
  'MU',
  'MW',
  'MZ',
  'NA',
  'NE',
  'NG',
  'RW',
  'SC',
  'SL',
  'SN',
  'SO',
  'SS',
  'ST',
  'SZ',
  'TD',
  'TG',
  'TZ',
  'UG',
  'ZA',
  'ZM',
  'ZW',
]);

countries.registerLocale(enLocale);

export const countryIso2TranslationKey: Record<string, string> = Object.fromEntries(
  Object.entries(countries.getNames('en')).map(([iso2, name]) => [
    iso2.toString().toLowerCase(),
    name,
  ]),
);

const regionMapObject: Record<RegionGroup, Set<string>> = {
  [RegionGroup.GCC]: GCC_COUNTRIES,
  [RegionGroup.MENA]: MENA_COUNTRIES,
  [RegionGroup.LATAM]: LATAM_COUNTRIES,
  [RegionGroup.APAC]: APAC_COUNTRIES,
  [RegionGroup.NORTH_AMERICA]: NORTH_AMERICA_COUNTRIES,
  [RegionGroup.SUB_SAHARAN_AFRICA]: SUB_SAHARAN_AFRICA_COUNTRIES,
};

export const regionTranslationKey: Record<RegionGroup, string> = {
  [RegionGroup.APAC]: 'APAC',
  [RegionGroup.GCC]: 'GCC',
  [RegionGroup.LATAM]: 'LATAM',
  [RegionGroup.MENA]: 'MENA',
  [RegionGroup.NORTH_AMERICA]: 'North America',
  [RegionGroup.SUB_SAHARAN_AFRICA]: 'Sub Saharan Africa',
};

export const regionMap = Object.keys(regionTranslationKey);

export const getRegionByCountry = (iso2: string) => {
  const code = iso2.toUpperCase();

  for (const [region, countries] of Object.entries(regionMapObject) as [
    RegionGroup,
    Set<string>,
  ][]) {
    if (countries.has(code)) return region;
  }

  return null;
};

export const hasFullRegion = (region: RegionGroup, selectedIso2: string[]) => {
  const regionCountries = Array.from(regionMapObject[region]);
  const selectedSet = new Set(selectedIso2.map((c) => c.toUpperCase()));

  for (const code of regionCountries) {
    if (!selectedSet.has(code as Uppercase<string>)) return false;
  }

  return true;
};

export const hasAnyFullRegion = (selectedIso2: string[]): null | RegionGroup => {
  const selectedSet = new Set(selectedIso2.map((c) => c.toUpperCase()));

  for (const [region, countries] of Object.entries(regionMapObject) as [
    RegionGroup,
    Set<string>,
  ][]) {
    let isFull = true;

    for (const code of Array.from(countries)) {
      if (!selectedSet.has(code as Uppercase<string>)) {
        isFull = false;
        break;
      }
    }

    if (isFull) return region;
  }

  return null;
};

export const getFullyCoveredRegions = (selectedIso2: string[]) => {
  const selectedSet = new Set(selectedIso2.map((c) => c.toUpperCase()));

  return (Object.entries(regionMapObject) as [RegionGroup, Set<string>][]).reduce<RegionGroup[]>(
    (acc, [region, countries]) => {
      for (const code of Array.from(countries)) {
        if (!selectedSet.has(code as Uppercase<string>)) return acc;
      }
      acc.push(region);
      return acc;
    },
    [],
  );
};
